import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule, DOCUMENT } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { Component, HostListener, Input, inject } from '@angular/core';
import type { FormGroup } from '@angular/forms';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TrackElementDirective } from '@core-mkt/directives/track-element/track-element.directive';
import { CookieService } from '@core-mkt/services/cookie/cookie.service';
import { CouponScrollService } from '@core-mkt/services/coupon-scroll/coupon-scroll.service';
import { ElementTrackingService } from '@core-mkt/services/element-tracking/element-tracking.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { IterableService } from '@core-mkt/services/iterable/iterable.service';
import { PricingService } from '@core-mkt/services/pricing/pricing.service';
import { StorageService } from '@core-mkt/services/storage/storage.service';
import { MarketingSyncUserService } from '@core-mkt/services/sync-user/marketing-sync-user.service';
import { VerticalService } from '@core-mkt/services/vertical/vertical.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import type { FormPayload } from '@core-mkt/shared/components/email-form/email-form/email-form';
import * as confetti from 'canvas-confetti';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EmailCapture } from './email-capture';

// The three values the modal-first variant differs from the slide-up on.
const MODAL_SCROLL_TRIGGER_PX = 500; // fire the modal after ~500px of scroll (SCROLL_TRIGGER_PX)
const MODAL_ITERABLE_LIST_ID = 10719394; // dedicated CRO list, distinct from the brand lists below
const MODAL_CONFIRMATION_DISMISS_MS = 6000; // post-submit sticky confirmation auto-dismiss delay
// The "$5 OFF" modal + tab is brand-scoped. Craft's displayMode alone can't opt a brand in — AA's
// entry was set to modalThenSlideUp and got the $5 tab over a CAREER20 coupon.
const MODAL_BRANDS = ['ACE', 'DEC'];
// DEC's $5-off modal uses ACE's frosted panel with DEC's gold button and tab. Its bottom
// slide-up is untouched.
const DEC_THEME_MODAL_BRANDS = ['DEC'];
// Modal brands whose design drops the confetti on submit. Kept apart from the theme list so a
// styling change can't silently switch confetti on or off.
const NO_CONFETTI_MODAL_BRANDS = ['DEC'];

@Component({
  selector: 'email-capture',
  templateUrl: './email-capture.component.html',
  styleUrls: ['./email-capture.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TrackElementDirective],
})
export class EmailCaptureComponent implements OnInit, OnDestroy {
  @Input() data: EmailCapture;
  @Input() stickyEmailOverride?: number;
  emailForm: FormGroup;
  isSubmited = false;
  /**
   * A submit on this page, as opposed to a returning visitor's cookie (which also sets
   * `isSubmited`). Keeps the confirmation up past the scroll trigger (EPS-4801).
   */
  private submittedThisPage = false;
  isEmailInvalid = false;
  window: Window;
  buttonLabelDefault = 'Get My Discount';
  display: string;
  displayConfetti = false;
  displayAfterSubmit: string;
  couponDefault = 'CAREER20';
  dismissedString = 'dismissed-';
  path: string;
  showEmailCapture = false;
  ambassador: boolean;
  isLoggedIn = false;
  emailMinimized = false;
  useInverseSlideup = false;
  isMinimized = false;
  isMobile = false;
  now: number;
  saleActive = false;

  // Modal-first variant (displayMode === 'modalThenSlideUp')
  modalActive = false; // centered modal (with scrim) currently showing
  modalDismissed = false; // user collapsed it this page → don't re-fire on scroll
  suppressTransition = false; // briefly disables CSS transitions to snap the modal→slide-up layout change
  // Read once at init (never mutated mid-page) so writing the session flag when the modal fires
  // suppresses it on the NEXT page without hiding the instance that is currently on screen.
  modalSeenThisSession = false;
  // The minimized left-edge "$5 OFF" tab left behind by a pre-submit dismiss.
  showStickyTab = false;
  // Set when the modal is reopened from that tab, so the once-per-session gate in
  // setEmailCapture() can't immediately re-hide it on the next scroll.
  modalForceOpen = false;
  // True once the modal has rendered on THIS page. modalSeenThisSession is cached at init, so it
  // only suppresses later navigations; without this the entrance replays every time the user
  // re-crosses the trigger on one page.
  private modalFiredThisPage = false;
  private confirmationDismissTimer: ReturnType<typeof setTimeout> | null = null;
  // applyDiscountSuccess()/setOffConfetti() timers, tracked so ngOnDestroy can cancel them — the
  // confetti callback looks #canvas up off the document and throws if the component is gone.
  private pricingEventTimer: ReturnType<typeof setTimeout> | null = null;
  private confettiStartTimer: ReturnType<typeof setTimeout> | null = null;
  private confettiStopTimer: ReturnType<typeof setTimeout> | null = null;

  // Cache urgency-related computed values
  hasUrgencyData = false;
  effectivePercentage = 20;
  effectiveCountdownDays = 3;
  urgencyMessage = '';
  emailCaptureContentCached = '';

  private listIds = {
    AA: 94171,
    ACE: 5388805,
    DEC: 5039154,
    HSP: 10981041, // EPS-4479: dedicated "Email Sticky Capture" list
    INS: 5388813,
    MTG: 5388818,
    QA: 7054980,
  };
  private submissionDate: Date | null = null;

  private vertical = inject(VerticalService);
  private readonly formBuilder = inject(FormBuilder);
  private document = inject(DOCUMENT);
  private redactor = inject(WysiwygRedactorService);
  private iterable = inject(IterableService);
  private readonly storageService = inject(StorageService);
  private env = inject(EnvService);
  private route = inject(ActivatedRoute);
  private breakpointObserver = inject(BreakpointObserver);
  private pricingService = inject(PricingService);
  private couponScroll = inject(CouponScrollService);
  private cookie = inject(CookieService);
  private elementTracking = inject(ElementTrackingService);
  private readonly marketingSyncUserService = inject(MarketingSyncUserService);
  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.emailForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });
    this.window = this.document.defaultView;
  }

  /** EPS-4992: page URL sent to Iterable — query string and fragment dropped so UTMs/coupons/tokens never leave. */
  private get siteUrl(): string | undefined {
    const location = this.document.defaultView?.location;
    return location ? `${location.origin}${location.pathname}` : undefined;
  }

  get coupon(): string {
    return this.data?.coupon || this.couponDefault;
  }

  /**
   * True when the Craft `displayMode` opts this site into the modal-first variant AND the brand is
   * on MODAL_BRANDS. Undefined / 'slideUp', or any other brand, keeps the bottom slide-up behavior.
   */
  get isModalMode(): boolean {
    return this.data?.displayMode === 'modalThenSlideUp' && this.isModalBrand;
  }

  // Memoized: EnvService.get deep-clones the whole env, and isModalMode is read several times per
  // scroll event. Lazy rather than set in ngOnInit so the getter is right before init runs too.
  private isModalBrandCached?: boolean;
  private get isModalBrand(): boolean {
    this.isModalBrandCached ??= MODAL_BRANDS.includes(this.brandId);
    return this.isModalBrandCached;
  }

  // Memoized for the same reason: the getters below are read on every change detection.
  private brandIdCached?: string;
  private get brandId(): string {
    this.brandIdCached ??= this.env.get.brandConfig.id;
    return this.brandIdCached;
  }

  /**
   * DEC's modal styling (gold button and tab). Live rather than set once in ngOnInit, so it can't
   * fall out of step with isModalMode if `data` arrives after init.
   */
  get useDecModalTheme(): boolean {
    return this.isModalMode && DEC_THEME_MODAL_BRANDS.includes(this.brandId);
  }

  /** The frosted inverse panel: the inverse-brand list, plus DEC in modal mode only. */
  get useInversePanel(): boolean {
    return this.useInverseSlideup || this.useDecModalTheme;
  }

  private get skipsConfetti(): boolean {
    return this.isModalMode && NO_CONFETTI_MODAL_BRANDS.includes(this.brandId);
  }

  /** Set by close() — possibly in another browser tab, since it lives in localStorage. */
  private get isDismissed(): boolean {
    return this.storageService.get(`${this.dismissedString}-${this.coupon}`) === 'true';
  }

  /** sessionStorage key that gates the modal to one appearance per browser session, per coupon. */
  private get modalSessionKey(): string {
    return `emailModalShown-${this.coupon}`;
  }

  /** sessionStorage key that keeps the minimized "$5 OFF" tab up across navigation. */
  private get tabSessionKey(): string {
    return `emailOfferTab-${this.coupon}`;
  }

  get displayContent(): string {
    return this.display;
  }

  get emailError(): string {
    return 'Please enter a valid email';
  }

  get emailCaptureTitle(): string {
    return 'Limited-Time Sale: Save 20% Today!';
  }

  get emailCaptureContent(): string {
    return this.emailCaptureContentCached;
  }

  /**
   * Updates the cached email capture content
   */
  private updateEmailCaptureContent(): void {
    if (!this.isSubmited && !this.displayContent) {
      this.emailCaptureContentCached =
        "You scored 20% off your course! There's never been a better time to join the real estate industry, so launch your new career today. Deal expires Sunday.";
    } else if (this.isSubmited) {
      let content: string;

      // Add displayContentAfterSubmit if it exists
      if (this.displayContentAfterSubmit) {
        content = this.displayContentAfterSubmit;
      } else {
        content = 'Coupon code <strong>"' + this.coupon + '</strong>" will be automatically applied at checkout';
      }

      // Add urgency message below if urgency data exists
      if (this.hasUrgencyData && this.urgencyMessage) {
        content += this.urgencyMessage;
      }

      this.emailCaptureContentCached = content;
    } else {
      this.emailCaptureContentCached = '';
    }
  }

  get displayContentAfterSubmit(): string {
    return this.data?.displayContentAfterSubmit || '';
  }

  get buttonLabel(): string {
    return this.data?.buttonLabel || this.buttonLabelDefault;
  }

  get saleStart(): Date {
    return this.data?.saleStart;
  }

  get saleEnd(): Date {
    return this.data?.saleEnd;
  }

  get percentageOff(): number | undefined {
    return this.data?.percentage;
  }

  get daysToExpire(): number | undefined {
    return this.data?.countdownDays;
  }

  /**
   * Computes urgency-related values and caches them
   */
  private computeUrgencyValues(): void {
    this.hasUrgencyData =
      (this.data?.percentage !== undefined && this.data?.percentage !== null) ||
      (this.data?.countdownDays !== undefined && this.data?.countdownDays !== null);

    this.effectivePercentage = this.data?.percentage ?? 20;
    this.effectiveCountdownDays = this.data?.countdownDays ?? 3;

    this.updateUrgencyMessage();
  }

  /**
   * Updates the urgency message based on current state
   */
  private updateUrgencyMessage(): void {
    if (!this.isSubmited || !this.hasUrgencyData || !this.expirationDate) {
      this.urgencyMessage = '';
    } else {
      const daysUntilExpiration = Math.ceil(
        (this.expirationDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24),
      );

      if (daysUntilExpiration > 0) {
        const expirationDateString = this.expirationDate?.toLocaleDateString('en-US', {
          year: '2-digit',
          month: 'numeric',
          day: 'numeric',
        });
        this.urgencyMessage = `<p><strong>Hurry, this ${this.effectivePercentage}% off deal ends ${expirationDateString}</strong></p>`;
      } else {
        this.urgencyMessage = `<p><strong>This offer has expired.</strong></p>`;
      }
    }

    // Update the email capture content whenever urgency message changes
    this.updateEmailCaptureContent();
  }

  /**
   * Calculates the expiration date based on submission date and effective countdown days
   */
  get expirationDate(): Date | null {
    if (!this.submissionDate) {
      return null;
    }
    const expiration = new Date(this.submissionDate);
    expiration.setDate(expiration.getDate() + this.effectiveCountdownDays);
    return expiration;
  }

  /**
   * Gets the submission date from cookie or current date
   */
  get submissionDateFromCookie(): Date | null {
    const submissionDateStr = this.cookie.getCookie('emailSubmissionDate');
    return submissionDateStr ? new Date(submissionDateStr) : null;
  }

  get emailListInfo(): { listId: number; type: string } | null {
    const brandId = this.env.get.brandConfig.id as keyof typeof this.listIds;
    // Modal-first variant subscribes to the dedicated CRO list rather than the brand list.
    const listId = this.isModalMode ? MODAL_ITERABLE_LIST_ID : this.listIds[brandId];

    if (!listId) return null;

    // The Iterable proxy Lambda resolves its API key by brandId/type, keyed on the vertical app type
    // (ACE → 'DE', AA → 'RE'), not the site brand id — ACE/ACE has no key (EPS-4878).
    return {
      listId,
      type: this.vertical.getVerticalInfo().appType || brandId,
    };
  }

  ngOnInit(): void {
    // HSP is deliberately off this list — it keeps the pink/blue slide-up, not the inverse
    // variant. The emailCoupon gate below carries its own list; adding a brand here does not
    // opt it into that one.
    // This flag is not modal-specific: it feeds the shared panel, so ACE's presence here
    // restyles ACE's bottom slide-up on every page, not just the $5-off modal.
    this.useInverseSlideup = ['AA', 'ACE', 'INS', 'MTG'].includes(this.env.get.brandConfig.id);
    // Modal-first variant always opens fully — its minimized state is never preserved
    // page-to-page, so ignore the persisted emailMinimize cookie.
    if (this.isModalMode) {
      this.isMinimized = false;
      // Cache the session flag once; setEmailCapture() reads this stable value so an
      // already-visible modal is never hidden by the flag written when it first fired.
      this.modalSeenThisSession = this.storageService.getSession(this.modalSessionKey) === 'true';
      // The "$5 OFF" tab restore used to live here. It has to run after the sale dates are parsed
      // and the submitted state is known — see restoreStickyTab(), called at the end of ngOnInit.
    } else {
      this.getEmailMinimizeCookie();
    }
    const amb = this.route.snapshot.queryParamMap?.get('campaignid');
    this.now = new Date().getTime();
    if (amb === '33113') {
      this.ambassador = true;
    }
    this.path = this.vertical.getVerticalInfo().vertical;
    this.display = this.redactor.bypassSanitizer(this.data?.displayContent);
    this.displayAfterSubmit = this.redactor.bypassSanitizer(this.data?.displayContentAfterSubmit);

    this.breakpointObserver.observe(['(max-width: 767px)']).subscribe((result) => {
      if (result.matches) {
        this.isMobile = true;
      }
    });

    // Modal mode intentionally preserves emailCoupon: the captured coupon must survive the next
    // page load so it rides onto checkout links. The slide-up keeps clearing stale coupons for
    // the non-modal ACE-family brands.
    if (!this.isModalMode && !['AA', 'INS', 'MTG', 'QA'].includes(this.env.get.brandConfig.id)) {
      this.cookie.deleteCookie('emailCoupon');
    }

    // Email capture is the active capture on this page — clear any stale phone-capture
    // coupon so only the active capture's coupon rides onto checkout links.
    if (this.data?.showEmailCapture) {
      this.cookie.deleteCookie('phoneCoupon');
    }

    if (this.data?.saleStart) {
      this.data.saleStart = new Date(this.data.saleStart);
    }
    if (this.data?.saleEnd) {
      this.data.saleEnd = new Date(this.data.saleEnd);
    }

    // A coupon captured under a previous campaign can outlive it — the cookie runs 7 days, the
    // Craft config can change any day — and the two halves read different sources: pricing and
    // checkout read the cookie (coupon-aggregation.service), while this component reports
    // `coupon` (the configured code) to the pricing event. A mismatch quotes one code and checks
    // out with another.
    if (this.isModalMode) {
      const storedCoupon = this.getEmailCouponCookie();
      if (storedCoupon && storedCoupon !== this.coupon) {
        this.cookie.deleteCookie('emailCoupon');
        this.cookie.deleteCookie('emailSubmissionDate');
      }
    }

    // Detect if user has already submitted the form
    if (this.getEmailCouponCookie()?.toString()) {
      // Set coupon code to be displayed
      this.isSubmited = true;
      // Initialize submission date from cookie if available
      this.submissionDate = this.submissionDateFromCookie || new Date();
    }

    // Compute urgency values AFTER setting submission state
    this.computeUrgencyValues();

    // Order matters: this guard reads submissionDate and hasUrgencyData, so it has to run after
    // the cookie rehydrate above and after computeUrgencyValues(). Called any earlier both
    // operands are still at their initial values, the method is a no-op, and an expired coupon
    // rides to checkout for the cookie's full 7 days. phone-capture.component.ts has the same
    // ordering constraint.
    this.checkAndCleanExpiredCoupon();

    // Broadcast AFTER the expiry sweep — before it, an expired coupon would still price.
    if (this.isSubmited) {
      // Send coupon event to trigger pricing updates across all components
      this.pricingService.sendCouponEvent(this.coupon);
    }

    // Initialize email capture content
    this.updateEmailCaptureContent();
    this.restoreStickyTab();
    this.setEmailCapture(undefined, this.stickyEmailOverride);

    this.marketingSyncUserService.syncUserReady$.pipe(takeUntil(this.destroy$)).subscribe((response) => {
      if (response?.success && response.data?.isAuthenticated) {
        this.isLoggedIn = true;
        // The tab is restored before auth resolves, so it has to come down with the offer it
        // reopens — otherwise a logged-in user keeps a CTA into a capture they are gated out of.
        this.showStickyTab = false;
        this.setEmailCapture(undefined, this.stickyEmailOverride);
      }
    });
  }

  /**
   * Retrieves the value of the 'emailCoupon' cookie.
   * @returns The value of the 'emailCoupon' cookie, or null if the cookie is not found.
   */
  public getEmailCouponCookie(): string | null {
    const cookieValue = this.cookie.getCookie('emailCoupon');
    return cookieValue;
  }
  /**
   * Sets the email coupon cookie with the provided coupon value.
   */
  public setEmailCouponCookie(): void {
    this.cookie.setCookie('emailCoupon', this.coupon);
  }
  /**
   * Sets the email submission date cookie with the current date.
   */
  public setEmailSubmissionDateCookie(): void {
    const now = new Date();
    this.cookie.setCookie('emailSubmissionDate', now.toISOString());
  }

  /**
   * Checks if the coupon has expired and cleans up cookies if necessary
   */
  public checkAndCleanExpiredCoupon(): void {
    if (this.hasUrgencyData && this.submissionDate) {
      const expirationDate = new Date(this.submissionDate);
      expirationDate.setDate(expirationDate.getDate() + this.effectiveCountdownDays);

      if (new Date() > expirationDate) {
        // Coupon has expired, clean up cookies
        this.cookie.deleteCookie('emailCoupon');
        this.cookie.deleteCookie('emailSubmissionDate');
        this.isSubmited = false;
        this.submissionDate = null;
        // Recompute urgency message after cleanup (this will also update email content)
        this.updateUrgencyMessage();
      }
    }
  }

  /**
   * Retrieves the value of the 'emailMinimize' cookie.
   * @returns The value of the 'emailMinimize' cookie, or null if the cookie is not found.
   */
  public getEmailMinimizeCookie(): void {
    const cookieValue = this.cookie.getCookie('emailMinimize');
    this.isMinimized = cookieValue === 'true';
  }

  /**
   * Sets the email form state cookie with the provided state value.
   * @param state The state of the email form (e.g., 'minimized' or 'expanded').
   */
  public setEmailMinimizeCookie(state: string): void {
    this.cookie.setCookie('emailMinimize', state);
  }

  /**
   * The modal covers the page with a scrim, so it needs a dismiss path that isn't the mouse.
   * Mirrors the chevron: Escape collapses it to the "$5 OFF" tab, same as clicking minimize.
   */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isModalMode && this.modalActive && !this.isSubmited) {
      this.toggleSize();
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    const paramOveride = this.route.snapshot.queryParamMap?.get('showCapture');
    if (paramOveride && paramOveride === 'false') {
      this.setEmailCapture(false, this.stickyEmailOverride);
    } else {
      this.setEmailCapture(undefined, this.stickyEmailOverride);
    }
  }

  /**
   * Checks if the sale is currently active based on the sale start and end dates.
   * @returns {boolean} True if the sale is active, false otherwise.
   */
  isSaleActive(): boolean {
    if (this.data?.saleStart && this.data?.saleEnd) {
      const saleStartTime = this.saleStart.getTime();
      const saleEndTime = this.saleEnd.getTime();
      if (this.now < saleEndTime.valueOf() && this.now > saleStartTime.valueOf()) {
        return true;
      }
    }
    return false;
  }

  /**
   * A dismiss earlier this session leaves the "$5 OFF" tab up across navigation. The session flag
   * alone is not enough to restore it — the flag outlives the conditions that allowed the offer.
   * Without re-checking them, a visitor who has since logged in, or already claimed the coupon, or
   * landed during a sitewide sale gets a tab that should not be there or that opens nothing.
   */
  private restoreStickyTab(): void {
    if (!this.stickyTabAllowed) {
      return;
    }
    this.showStickyTab = true;
    // modalDismissed rides along so a scroll on this page can't pop the modal back over the tab.
    this.modalDismissed = true;
  }

  /**
   * Whether the session's "$5 OFF" tab may be shown on this page. The session flag alone is not
   * enough: it outlives the conditions that allowed the offer, so every path that raises the tab
   * from storage (init, and the fallback in syncModalState) re-checks the display gates here. That
   * includes the dismissed flag: it is localStorage, so a close() in another browser tab never
   * cleared this tab's sessionStorage flag, and the tab would keep reopening a modal that the
   * dismissed gate immediately takes back down.
   */
  private get stickyTabAllowed(): boolean {
    return (
      this.isModalMode &&
      !!this.data?.showEmailCapture &&
      !this.isSubmited &&
      !this.isLoggedIn &&
      !this.ambassador &&
      !this.isSaleActive() &&
      !this.isDismissed &&
      this.storageService.getSession(this.tabSessionKey) === 'true'
    );
  }

  setEmailCapture(overide?: boolean, stickyEmailOverride?: number): void {
    const pixelsOverride = stickyEmailOverride ? stickyEmailOverride : this.isModalMode ? MODAL_SCROLL_TRIGGER_PX : 100;
    this.saleActive = this.isSaleActive();
    const minimizedValue = this.cookie.getCookie('emailMinimize');
    if (overide !== undefined) {
      this.showEmailCapture = overide;
      if (!overide && this.isModalMode) {
        // An explicit hide (the ?showCapture=false escape hatch) has to retire the post-submit
        // confirmation too. Otherwise syncModalState() reads the still-pending timer as "the
        // confirmation is owed" and puts it straight back for the rest of the 6s window.
        this.clearConfirmationTimer();
        this.modalActive = false;
        this.showStickyTab = false;
      }
      this.syncModalState();
      return;
    }
    this.showEmailCapture =
      !this.ambassador &&
      !this.isLoggedIn &&
      !this.saleActive &&
      this.data?.showEmailCapture &&
      !this.isDismissed &&
      // In modal mode the tab IS the dismissed presentation, so the panel must not also render.
      // Without this, any scroll after a dismiss re-raises showEmailCapture here while
      // syncModalState() refuses to re-raise modalActive, and the slide-up paints under the tab.
      // Guarded on isModalMode, and showStickyTab is only set inside modal-mode branches, so the
      // slide-up brands are untouched.
      !(this.isModalMode && this.showStickyTab) &&
      !(this.isModalMode && this.modalSeenThisSession && !this.modalForceOpen) &&
      // Same "fires once" rule, same-page half — modalSeenThisSession is cached at init and only
      // covers later navigations. Gated on !modalActive so an ordinary scroll with the modal open
      // doesn't tear down what it is protecting.
      !(this.isModalMode && this.modalFiredThisPage && !this.modalActive && !this.modalForceOpen) &&
      // The scroll trigger is one-way in modal mode: once the modal is up, scroll position no longer
      // owns it. Without the latch, any scroll event at or under the trigger — scrolling back up, or
      // momentum/rubber-band jitter right at 500px — tore it down, and the per-page and per-session
      // gates above then kept it from ever returning. Only the user's own dismiss paths and the
      // display gates above take it down. modalActive is only ever set in modal mode, so the
      // slide-up is untouched.
      // After a submit on this page the confirmation stays up until the visitor closes it: the
      // scroll-back to the priced component can land above the trigger (a hero Top Section), and the
      // visitor's next scroll there must not hide it while the price roll-down plays.
      (stickyEmailOverride === 0 || this.window.scrollY > pixelsOverride || this.modalActive || this.submittedThisPage);

    this.syncModalState();

    if (!this.isModalMode && minimizedValue !== 'true') {
      if (this.window.scrollY < 100) {
        this.isMinimized = false;
      }
    }
  }

  /**
   * Keeps the modal-first variant in lockstep with `showEmailCapture` (same fire timing as
   * the slide-up). Activates the centered modal when the capture becomes visible and the user
   * hasn't already collapsed it on this page; tears it down whenever the capture is hidden.
   */
  private syncModalState(): void {
    // In modal mode the post-submit confirmation is shown exactly once — by applyDiscountSuccess()
    // — and lives only while its auto-dismiss timer is pending. A later recompute must never revive
    // it: not as the scrimmed modal after the timer fired, and not for a returning visitor whose
    // 7-day emailCoupon cookie outlived the session flag. The discount is unaffected either way;
    // product-builder.service and coupon-aggregation.service read the cookie straight into the
    // price request without consulting this component.
    if (this.isModalMode && this.isSubmited) {
      this.modalActive = false;
      // The offer is claimed, so the tab must not outlive it. applyDiscountSuccess() already
      // clears it for an in-page submit; this covers a tab restored from the session flag by a
      // load that also found the cookie, whose click would otherwise open nothing.
      this.showStickyTab = false;
      this.showEmailCapture = this.confirmationDismissTimer !== null;
      return;
    }
    if (this.isModalMode && this.showEmailCapture && !this.modalDismissed) {
      if (!this.modalActive) {
        // First fire on this page → persist the per-session flag so the modal stays suppressed
        // on later navigations. modalSeenThisSession (cached at init) is deliberately left
        // untouched so the instance currently on screen keeps rendering.
        this.storageService.setSession(this.modalSessionKey, 'true');
      }
      this.modalActive = true;
      this.modalFiredThisPage = true;
    }
    if (!this.showEmailCapture) {
      this.modalActive = false;
      // Scroll can no longer hide an open modal (see the latch in setEmailCapture()); what still can
      // is the explicit ?showCapture=false override or a display gate. A modal reopened from the
      // tab has already cleared the tab, so fall back to the one the session flag says the user
      // still has — stickyTabAllowed re-checks the gates, so a gate that hid the modal keeps the
      // tab down too.
      if (this.stickyTabAllowed) {
        this.restoreStickyTab();
        this.modalForceOpen = false;
      }
    }
  }

  async submit(): Promise<void> {
    const emailRegEx = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/g;
    const formValue = this.emailForm.value as { email: string };
    const emailValue = String(formValue.email ?? '');
    if (!emailRegEx.test(emailValue)) {
      this.isEmailInvalid = true;

      // Track validation error
      this.elementTracking.trackFormError(
        'page_emailcapture_signup',
        'email_form',
        'footer',
        'VALIDATION_ERROR',
        'Please enter a valid email',
        {
          email: emailValue,
        },
      );

      return;
    }
    this.isEmailInvalid = false;
    const iterableData: FormPayload = {
      listId: this.emailListInfo.listId,
      subscribers: [
        {
          dataFields: {
            brandId: this.env.get.brandConfig.id,
            siteUrl: this.siteUrl,
          },
          email: emailValue,
        },
      ],
      type: this.emailListInfo.type,
    };

    // Prepare element_value for tracking
    const elementValue = {
      subscription_list_id: this.emailListInfo.listId,
      email: emailValue,
      coupon_code: this.coupon,
      brand_id: this.env.get.brandConfig.id,
      has_urgency: this.hasUrgencyData,
      ...(this.hasUrgencyData &&
        this.effectiveCountdownDays !== undefined && { countdown_days: this.effectiveCountdownDays }),
      ...(this.hasUrgencyData &&
        this.effectivePercentage !== undefined && { percentage_off: this.effectivePercentage }),
    };

    try {
      await this.iterable.addToMailList(iterableData, this.env.get.brandConfig.id);

      // Track successful submission
      this.elementTracking.trackFormSubmit('page_emailcapture_signup', 'email_form', 'footer', elementValue, true);
      this.applyDiscountSuccess();
    } catch (error) {
      // Track submission error
      // IterableService throws standard Error objects with only message and stack properties
      const errorMessage =
        error instanceof Error ? error.message : 'There was an issue with your subscription. Please try again later.';
      this.elementTracking.trackFormError(
        'page_emailcapture_signup',
        'email_form',
        'footer',
        'SUBMISSION_ERROR',
        errorMessage,
        elementValue,
      );
      // Modal mode completes the discount experience even when Iterable fails — the email is
      // best-effort but the captured coupon must still apply at checkout. The slide-up keeps its
      // original behavior (form stays open so the user can retry).
      if (this.isModalMode) {
        this.applyDiscountSuccess();
      } else {
        this.isSubmited = false;
      }
    }
  }

  /**
   * Completes the post-capture discount experience: marks submitted, persists the coupon and
   * submission-date cookies, broadcasts the pricing event, fires confetti, and (modal-first
   * variant) converts the banner into an auto-dismissing sticky confirmation. Shared by the
   * Iterable success path and, for the modal variant, the failure path.
   */
  private applyDiscountSuccess(): void {
    this.isSubmited = true;
    this.submittedThisPage = true;
    // Set submission date in memory for immediate urgency calculation
    this.submissionDate = new Date();
    // Persist the coupon + submission-date cookies (coupon rides onto checkout links)
    this.setEmailCouponCookie();
    this.setEmailSubmissionDateCookie();

    // Send coupon event to trigger pricing updates across all components. The small delay ensures
    // the cookies are set before the refresh reads them.
    this.pricingEventTimer = setTimeout(() => {
      this.pricingEventTimer = null;
      this.pricingService.sendCouponEvent(this.coupon);
    }, 100);
    // Scroll back to the highest-priority priced component (Pricing Table > Bundles > Top Section);
    // Top Section starts its roll-down once this settles (EPS-4801).
    void this.couponScroll.scrollToPricedComponent();
    if (!this.skipsConfetti) {
      this.setOffConfetti();
    }
    // Recompute urgency message after submission (this also updates email content)
    this.updateUrgencyMessage();

    // Modal mode: drop the centered scrim so the success state reads as a bottom sticky
    // confirmation, then auto-dismiss it after a short delay.
    if (this.isModalMode) {
      this.modalActive = false;
      // The offer has been claimed — retire the minimized tab and its session flag.
      this.showStickyTab = false;
      this.modalForceOpen = false;
      this.storageService.setSession(this.tabSessionKey, 'false');
      this.startConfirmationAutoDismiss();
    }
  }

  setOffConfetti(): void {
    this.displayConfetti = true;

    this.confettiStartTimer = setTimeout(() => {
      this.confettiStartTimer = null;
      const canvas = document.getElementById('canvas') as HTMLCanvasElement;
      const createFn = (confetti as unknown as { create: (el: HTMLCanvasElement) => (opts: object) => Promise<null> })
        .create;

      void createFn(canvas)({
        shapes: ['square'],
        particleCount: 100,
        spread: 40,
        scalar: 0.5,
        origin: {
          y: 1,
          x: 0.5,
        },
      });
    }, 500);

    this.confettiStopTimer = setTimeout(() => {
      this.confettiStopTimer = null;
      this.displayConfetti = false;
    }, 5000);
  }

  /**
   * Modal-first variant only: hides the post-submit sticky confirmation after a short delay so it
   * does not linger on the page.
   */
  private startConfirmationAutoDismiss(): void {
    this.clearConfirmationTimer();
    this.confirmationDismissTimer = setTimeout(() => {
      // Null the handle first: syncModalState() reads it as "the confirmation is still owed".
      this.confirmationDismissTimer = null;
      this.showEmailCapture = false;
      this.modalActive = false;
    }, MODAL_CONFIRMATION_DISMISS_MS);
  }

  private clearPostSubmitTimers(): void {
    [this.pricingEventTimer, this.confettiStartTimer, this.confettiStopTimer].forEach((handle) => {
      if (handle) {
        clearTimeout(handle);
      }
    });
    this.pricingEventTimer = null;
    this.confettiStartTimer = null;
    this.confettiStopTimer = null;
  }

  private clearConfirmationTimer(): void {
    if (this.confirmationDismissTimer) {
      clearTimeout(this.confirmationDismissTimer);
      this.confirmationDismissTimer = null;
    }
  }

  //minimize function
  toggleSize(): void {
    if (this.isModalMode) {
      // A pre-submit dismiss collapses the modal to a persistent left-edge "$5 OFF" tab, not the
      // bottom slide-up. The tab flag is session-scoped so it survives navigation; clicking the
      // tab reopens the modal. The centered→collapsed change is snapped (transitions suppressed
      // for a frame) so it does not animate as a diagonal slide.
      this.modalActive = false;
      this.modalDismissed = true;
      this.modalForceOpen = false;
      this.showEmailCapture = false;
      this.showStickyTab = true;
      this.storageService.setSession(this.tabSessionKey, 'true');
      this.snapTransition();
      return;
    }
    // Only the slide-up has a minimized presentation, so this flip has to stay below the
    // modal-mode return — above it, isMinimized sticks true for the rest of the page and the
    // panel renders 76%-translated instead of full height.
    this.isMinimized = !this.isMinimized;
    this.setEmailMinimizeCookie(this.isMinimized ? 'true' : 'false');
  }

  /**
   * Reopens the modal from the minimized "$5 OFF" tab. Forces it open so a subsequent scroll does
   * not re-trigger the once-per-session suppression.
   */
  reopenFromTab(): void {
    // setEmailCapture(true) takes the override branch, which returns before the whole predicate —
    // so every display gate is skipped. The tab can outlive those gates (a login, a sale starting,
    // the coupon already claimed, a close() in another browser tab), and reopening past them would
    // let a user submit into an offer they are excluded from, or stack this coupon on top of a
    // sitewide sale.
    if (
      this.isLoggedIn ||
      this.ambassador ||
      this.isSubmited ||
      this.isSaleActive() ||
      this.isDismissed ||
      !this.data?.showEmailCapture
    ) {
      this.showStickyTab = false;
      this.storageService.setSession(this.tabSessionKey, 'false');
      return;
    }
    this.showStickyTab = false;
    this.modalDismissed = false;
    this.modalForceOpen = true;
    this.setEmailCapture(true);
    // The panel's Tailwind `transition-all` would otherwise interpolate the slide-up geometry
    // into the centered-modal geometry, which reads as the modal flying in from the RIGHT. Snap
    // that layout change so the left-origin keyframe owns the motion. `modalForceOpen` doubles as
    // the "this open came from the tab" flag for that keyframe — toggleSize()/close() clear it, so
    // the class is removed and re-added on each reopen, which is what re-triggers the animation.
    this.snapTransition();
  }

  /**
   * Suppresses CSS transitions for a frame so a one-off layout change snaps instantly,
   * then re-enables them so the slide-up's later minimize/expand still animate.
   */
  private snapTransition(): void {
    this.suppressTransition = true;
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.suppressTransition = false;
        });
      });
    } else {
      this.suppressTransition = false;
    }
  }

  close(): void {
    this.storageService.set(`${this.dismissedString}-${this.coupon}`, 'true');
    // An explicit close retires the confirmation early. Without this the timer stays pending and
    // syncModalState() would read it as "still owed" and re-show what the user just closed.
    this.clearConfirmationTimer();
    this.showEmailCapture = false;
    // Tear the scrim down with the banner. Without this, the `@if (modalActive)` backdrop
    // lingers until the next scroll re-runs syncModalState(). Mirrors toggleSize().
    this.modalActive = false;
    this.modalDismissed = true;
    // Fully closing also clears the minimized "$5 OFF" tab and its session flag.
    this.modalForceOpen = false;
    this.showStickyTab = false;
    this.storageService.setSession(this.tabSessionKey, 'false');
  }

  ngOnDestroy(): void {
    this.clearConfirmationTimer();
    // A navigation inside the 500ms confetti window would otherwise run createFn() against a
    // #canvas that is no longer in the document, which throws from inside the timeout.
    this.clearPostSubmitTimers();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
